import { pick } from '@eturi/util'
import isEmpty from 'lodash/isEmpty.js'
import type {
	BlockState,
	BlockStatus,
	DeviceBlockStatus,
	IntendedBlockState,
	PrunedRawBlockStatusKeys,
	PrunedRawDeviceBlockStatusKeys,
	RawBlockStatus,
	RawDeviceBlockStatus,
	ViewBlockState,
} from '../types/BlockStatus.js'
import type { RawUsage } from '../types/Usage.js'
import { getUsageDisplayState, mapRawBlockStatusToUsage } from './usage.js'

const PRUNED_RAW_BLOCK_STATUS_KEYS = [
	'applied_state',
	'rule',
	'state',
	'user_id',
] as const satisfies PrunedRawBlockStatusKeys[]

const PRUNED_RAW_DEVICE_BLOCK_STATUS_KEYS = [
	'device_id',
	'os',
	'status',
] as const satisfies PrunedRawDeviceBlockStatusKeys[]

export const getIntendedBlockState = (
	blockState: BlockState,
	viewState: ViewBlockState,
): IntendedBlockState => {
	switch (viewState) {
		case 'not_paired':
		case 'paused':
			return viewState

		case 'pending':
			return (
				blockState == 'allowing' ? 'allowed'
				: blockState == 'blocking' ? 'blocked'
				: 'paused'
			)
	}

	return viewState
}

export const mapRawToBlockStatus = (raw: RawBlockStatus): BlockStatus => {
	/**
	 * Normalize and map RawUsage->Usage. If usage is superceded by another rule (e.g. schedule or
	 * manual block), set it to null. Otherwise, map it.
	 *
	 * FIXME: We should verify that Usage isn't required elsewhere. For example, in calculating
	 * totals for the day.
	 */
	const usage = isUsageSuperceded(raw) ? null : mapRawBlockStatusToUsage(raw)

	// Get base decorated status type of block state
	let viewState = getViewBlockState(raw)

	// Decorate device statuses: Map viewState to each device
	const deviceStatuses = raw.devices.map((d) => mapRawToDeviceBlockStatus(raw, d))

	// Determine whether we're not_paired if there are no devices, or if all
	// devices are not paired
	const isNotPaired =
		isEmpty(deviceStatuses) || deviceStatuses.every((s) => s.viewState == 'not_paired')

	// Block status is not_paired if so
	viewState = isNotPaired ? 'not_paired' : viewState

	return {
		...pick(raw, PRUNED_RAW_BLOCK_STATUS_KEYS),
		devices: deviceStatuses,
		intendedState: getIntendedBlockState(raw.state, viewState),
		usage,
		viewState,
	}
}

const mapRawToDeviceBlockStatus = (
	rawBlockStatus: RawBlockStatus,
	rawDeviceBlockStatus: RawDeviceBlockStatus,
): DeviceBlockStatus => {
	const viewState = getDeviceViewBlockState(rawBlockStatus, rawDeviceBlockStatus)

	return {
		...pick(rawDeviceBlockStatus, PRUNED_RAW_DEVICE_BLOCK_STATUS_KEYS),
		intendedState: getIntendedBlockState(rawDeviceBlockStatus.status, viewState),
		vewStatus: rawDeviceBlockStatus.view_status || '',
		viewState,
	}
}

const getDeviceViewBlockState = (
	rawBlockStatus: RawBlockStatus,
	rawDeviceBlockStatus: RawDeviceBlockStatus,
): ViewBlockState => {
	const { device_id, status } = rawDeviceBlockStatus

	// If device is not paired, other states don't matter
	if (status == 'management removed') return 'not_paired'

	// We change "blocking" or "allowing" to "pending"
	if (status == 'allowing' || status == 'blocking') return 'pending'

	// If allowance applied and state is known (if not_paired/pending previous
	// conditions will apply). If we have usage info, get the state of the device
	// from that
	if (isAllowanceBlockStatus(rawBlockStatus)) {
		switch (rawBlockStatus.usage.requested_state) {
			case 'expired':
				// Expired usage is blocked
				return 'blocked'

			case 'pause':
				// Device is "paused" if `requested_state` is in "pause" and `status`
				// is `blocked`. Otherwise, it's pending
				return status === 'blocked' ? 'paused' : 'pending'

			case 'play':
				return rawBlockStatus.usage.devices_in_use?.includes(device_id) ? 'allowed' : 'pending'
		}
	}

	return status == 'blocked' ? 'blocked' : 'allowed'
}

const getViewBlockState = (raw: RawBlockStatus): ViewBlockState => {
	switch (raw.state) {
		case 'management removed':
			return 'not_paired'

		case 'allowing':
		case 'blocking':
			return 'pending'

		case 'blocked':
			return isAllowanceBlockStatus(raw) && getUsageDisplayState(raw.usage) == 'paused' ?
					'paused'
				:	'blocked'
	}

	return 'allowed'
}

/**
 * Whether we're blocked by something that supercedes allowance.
 */
const isUsageSuperceded = ({ rule, state }: RawBlockStatus) =>
	state == 'blocked' && (rule?.type == 'temporal' || rule?.type == 'override')

/**
 * Determines whether BlockStatus is governed by allowance.
 *
 * TODO: I have no idea why 'temporal_default' was considered an 'allowance' rule. It seems like
 * it's just being used for calculating state (perhaps initial account state).
 */
const isAllowanceBlockStatus = (
	raw: RawBlockStatus,
): raw is RawBlockStatus & { readonly usage: RawUsage } =>
	(raw.rule?.type == 'allowance' || raw.rule?.type == 'temporal_default') &&
	raw.usage?.requested_state != null
